/**
 * MODULE: domain/pages/frontend/components/editor/PagesToggleBlockView.tsx
 * PURPOSE: Collapsible toggle block in the Pages note editor
 */

import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import type { KeyboardEvent } from 'react';

export function PagesToggleBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const editable = editor.isEditable;
  const title = String(node.attrs.title ?? 'Toggle');
  const body = String(node.attrs.body ?? '');
  const open = node.attrs.open !== false;

  const stopKeys = (event: KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <NodeViewWrapper
      className={`my-2 ${selected ? 'ring-2 ring-gray-300 ring-offset-2 dark:ring-gray-600 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-toggle-block"
      data-open={open ? 'true' : 'false'}
    >
      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950/40">
        <div className="flex items-start gap-1 px-2 py-1.5">
          <button
            type="button"
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-expanded={open}
            aria-label={open ? 'Collapse toggle' : 'Expand toggle'}
            onClick={() => updateAttributes({ open: !open })}
            data-testid="pages-toggle-chevron"
          >
            {open ? (
              <ChevronDownIcon className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronRightIcon className="h-4 w-4" aria-hidden />
            )}
          </button>
          {editable ? (
            <input
              type="text"
              value={title}
              onChange={(event) => updateAttributes({ title: event.target.value })}
              onKeyDown={stopKeys}
              placeholder="Toggle heading"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm font-medium text-gray-900 focus:outline-none focus:ring-0 dark:text-white"
              data-testid="pages-toggle-title-input"
            />
          ) : (
            <p className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-white">{title}</p>
          )}
        </div>
        {open ? (
          <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-800">
            {editable ? (
              <textarea
                value={body}
                onChange={(event) => updateAttributes({ body: event.target.value })}
                onKeyDown={stopKeys}
                placeholder="Hidden content…"
                rows={3}
                className="w-full resize-y border-0 bg-transparent text-sm text-gray-700 focus:outline-none focus:ring-0 dark:text-gray-200"
                data-testid="pages-toggle-body-input"
              />
            ) : (
              <p
                className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200"
                data-testid="pages-toggle-body-text"
              >
                {body}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}
